/**
 * Cross-cutting app state: language/direction, network status, and the user's own settings.
 *
 * Language resolves in three steps — the saved server setting wins, then whatever Telegram
 * reports for the account, then English. Whichever it lands on is written to <html lang/dir>
 * so every CSS logical property mirrors without a single per-component RTL branch.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { MeProfile } from '@promptvault/shared';
import { fetchMe } from '../api/me';
import { I18nContext, STRINGS, isRtl, normalizeLang, type Lang } from '../lib/i18n';
import { syncSafeArea, telegramLanguage, twa } from '../lib/telegram';
import { ToastProvider } from '../components/Toast';

const LANG_KEY = 'pv.lang';

export function AppProviders({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<Lang | null>(() => {
    if (typeof localStorage === 'undefined') return null;
    const stored = localStorage.getItem(LANG_KEY);
    return stored === 'en' || stored === 'fa' ? stored : null;
  });

  // `me` is fetched once here so language, NSFW and Pro state are available app-wide without
  // every screen re-requesting the profile.
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    staleTime: 60_000,
    retry: 1,
  });

  const lang: Lang =
    override ?? normalizeLang(me?.settings.language ?? telegramLanguage().slice(0, 2));
  const rtl = isRtl(lang);

  const setLang = useCallback((next: Lang) => {
    setOverride(next);
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch {
      /* private mode */
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.lang = lang;
    root.dir = rtl ? 'rtl' : 'ltr';
  }, [lang, rtl]);

  // Telegram hands us real viewport/safe-area numbers only after `ready()`; re-read on change.
  useEffect(() => {
    syncSafeArea();
    const w = twa();
    if (!w) return;
    const onChange = () => syncSafeArea();
    w.onEvent('viewportChanged', onChange);
    w.onEvent('safeAreaChanged', onChange);
    w.onEvent('contentSafeAreaChanged', onChange);
    return () => {
      w.offEvent('viewportChanged', onChange);
      w.offEvent('safeAreaChanged', onChange);
      w.offEvent('contentSafeAreaChanged', onChange);
    };
  }, []);

  const i18n = useMemo(() => ({ lang, rtl, t: STRINGS[lang], setLang }), [lang, rtl, setLang]);

  return (
    <I18nContext.Provider value={i18n}>
      <ToastProvider>{children}</ToastProvider>
    </I18nContext.Provider>
  );
}

/** Live online/offline flag — drives the offline banner and stops pointless refetches. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

/** The cached profile, for screens that only need settings (NSFW, Pro) rather than the whole page. */
export function useMe(): MeProfile | undefined {
  const { data } = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 60_000, retry: 1 });
  return data;
}
